import { useState, useRef } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ScanBarcode,
  Search,
  Loader2,
  Package,
  Tag,
  DollarSign,
  Check,
  Plus,
  Camera,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Scanner() {
  const [code, setCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageScanning, setImageScanning] = useState(false);
  const [imageResult, setImageResult] = useState(null);
  const [addingProduct, setAddingProduct] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleBarcodeScan = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setScanning(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/ai/scan-barcode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: code.trim() }),
      });
      if (!res.ok) throw new Error("Scan failed");
      const data = await res.json();
      setResult(data);
      toast.success(data.identified ? "Product identified!" : "Could not identify product");
    } catch (e) {
      toast.error("AI scan failed. Please try again.");
    } finally {
      setScanning(false);
    }
  };

  const handleImageScan = async (file) => {
    if (!file) return;
    setImageScanning(true);
    setImageResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/api/ai/analyze-product`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!res.ok) throw new Error("Image analysis failed");
      const data = await res.json();
      setImageResult(data);
      toast.success("Image analyzed!");
    } catch (e) {
      toast.error("Image analysis failed. Please try again.");
    } finally {
      setImageScanning(false);
    }
  };

  const addToInventory = async (productData) => {
    setAddingProduct(true);
    try {
      const payload = {
        sku: productData.suggested_sku || productData.barcode || `SKU-${Date.now()}`,
        name: productData.name || "Unknown Product",
        barcode: productData.barcode || null,
        category: productData.category || "Other",
        brand: productData.brand || null,
        description: productData.description || "",
        cost_price: productData.estimated_cost_price || 0,
        retail_price: productData.estimated_retail_price || 0,
      };
      const res = await fetch(`${API}/api/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to create product");
      }
      toast.success(`"${payload.name}" added to products!`);
      navigate("/products");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAddingProduct(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="scanner-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            AI Smart Scanner
          </h1>
          <p className="text-muted-foreground">
            Scan any barcode, UPC, serial number, or product photo — AI identifies it instantly
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Barcode / Serial Number Scanner */}
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ScanBarcode className="h-5 w-5" />
                Barcode / Serial Lookup
              </CardTitle>
              <CardDescription>Enter a barcode, UPC, EAN, or serial number</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBarcodeScan} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="barcode">Barcode / Serial Number</Label>
                  <div className="flex gap-2">
                    <Input
                      id="barcode"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="e.g. 0194253715276 or SN-ABC123"
                      className="rounded-sm font-data flex-1"
                      data-testid="barcode-input"
                      autoFocus
                    />
                    <Button
                      type="submit"
                      disabled={scanning || !code.trim()}
                      className="rounded-sm"
                      data-testid="scan-barcode-btn"
                    >
                      {scanning ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </form>

              {scanning && (
                <div className="mt-6 text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-3" />
                  <p className="text-muted-foreground">AI is identifying this product...</p>
                </div>
              )}

              {result && (
                <div className="mt-6 space-y-4" data-testid="scan-result">
                  <div className="flex items-center gap-2">
                    {result.identified ? (
                      <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                        <Check className="h-3 w-3 mr-1" /> Identified
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Best Guess</Badge>
                    )}
                    {result.already_in_inventory && (
                      <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                        Already in Inventory
                      </Badge>
                    )}
                  </div>

                  <div className="p-4 rounded-md border bg-muted/30 space-y-3">
                    <h3 className="font-semibold text-lg">{result.name}</h3>
                    {result.brand && (
                      <p className="text-sm flex items-center gap-2">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{result.brand}</span>
                        {result.category && <Badge variant="outline">{result.category}</Badge>}
                      </p>
                    )}
                    {result.description && (
                      <p className="text-sm text-muted-foreground">{result.description}</p>
                    )}
                    {(result.estimated_retail_price > 0 || result.estimated_cost_price > 0) && (
                      <div className="flex gap-4">
                        {result.estimated_cost_price > 0 && (
                          <p className="text-sm flex items-center gap-1">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            Cost: <span className="font-data font-semibold">${result.estimated_cost_price?.toFixed(2)}</span>
                          </p>
                        )}
                        {result.estimated_retail_price > 0 && (
                          <p className="text-sm flex items-center gap-1">
                            <DollarSign className="h-4 w-4 text-emerald-500" />
                            Retail: <span className="font-data font-semibold text-emerald-600">${result.estimated_retail_price?.toFixed(2)}</span>
                          </p>
                        )}
                      </div>
                    )}
                    {result.specifications && (
                      <p className="text-xs text-muted-foreground">{result.specifications}</p>
                    )}
                  </div>

                  {!result.already_in_inventory && (
                    <Button
                      onClick={() => addToInventory(result)}
                      disabled={addingProduct}
                      className="w-full rounded-sm"
                      data-testid="add-scanned-product-btn"
                    >
                      {addingProduct ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Add to Products
                    </Button>
                  )}

                  {result.already_in_inventory && result.existing_product && (
                    <Button
                      variant="outline"
                      onClick={() => navigate("/products")}
                      className="w-full rounded-sm"
                    >
                      <Package className="h-4 w-4 mr-2" />
                      View in Products
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Image Scanner */}
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Camera className="h-5 w-5" />
                Photo Recognition
              </CardTitle>
              <CardDescription>Upload a product photo for AI identification</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setImageFile(file);
                      handleImageScan(file);
                    }
                  }}
                  data-testid="image-upload-input"
                />
                <div
                  className="border-2 border-dashed rounded-md p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="image-upload-area"
                >
                  {imageFile ? (
                    <div className="space-y-2">
                      <img
                        src={URL.createObjectURL(imageFile)}
                        alt="Preview"
                        className="max-h-40 mx-auto rounded-md"
                      />
                      <p className="text-sm text-muted-foreground">{imageFile.name}</p>
                    </div>
                  ) : (
                    <>
                      <Camera className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                      <p className="text-sm font-medium">Click to upload a product photo</p>
                      <p className="text-xs text-muted-foreground">PNG, JPG, WEBP up to 10MB</p>
                    </>
                  )}
                </div>
              </div>

              {imageScanning && (
                <div className="mt-6 text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-3" />
                  <p className="text-muted-foreground">AI is analyzing the image...</p>
                </div>
              )}

              {imageResult && (
                <div className="mt-6 space-y-4" data-testid="image-scan-result">
                  <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                    <Check className="h-3 w-3 mr-1" /> Analyzed
                  </Badge>

                  <div className="p-4 rounded-md border bg-muted/30 space-y-3">
                    <h3 className="font-semibold text-lg">{imageResult.name}</h3>
                    {imageResult.brand && (
                      <p className="text-sm">
                        <span className="font-medium">{imageResult.brand}</span>
                        {imageResult.category && (
                          <Badge variant="outline" className="ml-2">{imageResult.category}</Badge>
                        )}
                      </p>
                    )}
                    {imageResult.description && (
                      <p className="text-sm text-muted-foreground">{imageResult.description}</p>
                    )}
                  </div>

                  <Button
                    onClick={() => addToInventory({
                      name: imageResult.name,
                      brand: imageResult.brand,
                      category: imageResult.category,
                      description: imageResult.description,
                      suggested_sku: `SKU-${Date.now()}`,
                    })}
                    disabled={addingProduct}
                    className="w-full rounded-sm"
                    data-testid="add-image-product-btn"
                  >
                    {addingProduct ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Add to Products
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
